import { ChevronLeftIcon, ChevronRightIcon, DoubleArrowLeftIcon, DoubleArrowRightIcon } from "@radix-ui/react-icons";
import { Table } from "@tanstack/react-table";
import { cn } from "src/helpers";

import { Button, Select, SelectContent, SelectItem, SelectTrigger } from "src/shadcn/components/ui";

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  useQueryResponseLoading: any;
  useQueryRequest: any;
  useQueryResponsePagination: any;
}

export function DataTablePagination<TData>({
  useQueryRequest,
  useQueryResponseLoading,
  useQueryResponsePagination,
}: DataTablePaginationProps<TData>) {
  const pagination = useQueryResponsePagination();

  const { state, updateState } = useQueryRequest();

  const startIndex = (Number(pagination.page) - 1) * pagination.items_per_page + 1;
  const endIndex = Math.min(
    (startIndex === 1 ? 0 : startIndex) + pagination.items_per_page,
    parseInt(pagination.total_records || "0", 10)
  );

  return (
    <div className={cn("flex items-center justify-between px-2", { isLoading: "blur-sm pointer-events-none" })}>
      <div className="flex-1 text-sm text-muted-foreground">
        <div>
          Showing <span>{startIndex}</span> to <span>{endIndex}</span> of <span>{pagination?.total_records}</span>
        </div>
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8 ">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={pagination.items_per_page}
            onValueChange={(value) => {
              updateState({
                ...state,
                page: 1,
                items_per_page: Number(value) as any,
              });
            }}
          >
            <SelectTrigger onClear={() => {}} className="h-8 w-[70px]">
              <button>{pagination.items_per_page}</button>
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="iconBtn"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateState({
                ...state,
                page: 1,
                items_per_page: pagination.items_per_page,
              });
            }}
            disabled={Number(pagination.page) === 1}
          >
            <span className="sr-only">Go to first page</span>
            <DoubleArrowLeftIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="iconBtn"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateState({
                ...state,
                page: Number(pagination.page) !== 1 ? pagination.page - 1 : (1 as any),
                items_per_page: pagination.items_per_page,
              });
            }}
            disabled={Number(pagination.page) === 1}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <div className="flex w-[100px] items-center justify-center text-sm font-medium">
            Page {pagination.page} of {pagination.total}
          </div>
          <Button
            variant="iconBtn"
            className="h-8 w-8 p-0"
            onClick={() => {
              updateState({
                ...state,
                page: Number(pagination.page) !== Number(pagination.last_page) ? pagination.page + 1 : (1 as any),
                items_per_page: pagination.items_per_page,
              });
            }}
            disabled={Number(pagination.page) === Number(pagination.last_page)}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="iconBtn"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              updateState({
                ...state,
                page: Number(pagination.last_page) as any,
                items_per_page: pagination.items_per_page,
              });
            }}
            disabled={Number(pagination.page) === Number(pagination.last_page)}
          >
            <span className="sr-only">Go to last page</span>
            <DoubleArrowRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
